import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/axiosConfig';
import DateRangePicker from '@/components/ui/DateRangePicker'; 
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Eye, Pencil, Trash2, Search, ChevronLeft, ChevronRight, X, Printer} from 'lucide-react'; 
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import DetalhesVendaModal from '../components/DetalhesVendaModal'; 
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ITEMS_PER_PAGE = 20; 

// Estado inicial dos filtros
const initialFilters = {
  date: { from: null, to: null },
  categoria: '',
  vendedor_id: '',
  fornecedor_id: '',
  termo_busca: '', 
};

export default function Vendas() {
  const [vendas, setVendas] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [fornecedores, setFornecedores] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  const [filters, setFilters] = useState(initialFilters);
  const navigate = useNavigate();

  // Função para carregar vendas (agora aceita filtros como argumento)
  const loadVendas = async (filtersToLoad = filters) => {
    setLoading(true);
    setCurrentPage(1); 
    try {
      const params = {};
      
      // Monta os parâmetros com base nos filtros
      if (filtersToLoad.date?.from) {
          const localDate = new Date(filtersToLoad.date.from);
          const startDate = new Date(Date.UTC(
              localDate.getFullYear(),
              localDate.getMonth(),
              localDate.getDate(),
              0, 0, 0, 0 
          ));
          params.data_inicio = startDate.toISOString();
      }
      if (filtersToLoad.date?.to) {
          const localDate = new Date(filtersToLoad.date.to);
          const endDate = new Date(Date.UTC(
              localDate.getFullYear(),
              localDate.getMonth(),
              localDate.getDate(),
              23, 59, 59, 999 
          ));
          params.data_fim = endDate.toISOString();
      }
      
      if (filtersToLoad.categoria) params.categoria = filtersToLoad.categoria;
      if (filtersToLoad.vendedor_id) params.vendedor_id = filtersToLoad.vendedor_id;
      if (filtersToLoad.fornecedor_id) params.fornecedor_id = filtersToLoad.fornecedor_id; 
      if (filtersToLoad.termo_busca) params.termo_busca = filtersToLoad.termo_busca;

      const response = await axiosInstance.get('/vendas', { params });
      
      const fetchedVendas = response.data || [];
      // Ordena por data (mais recentes primeiro)
      const sortedVendas = fetchedVendas.sort((a, b) => {
        return new Date(b.data_venda) - new Date(a.data_venda);
      });
      setVendas(sortedVendas);

    } catch (error) {
      toast.error('Erro ao carregar vendas');
      console.error("Erro detalhado ao carregar vendas:", error.response || error);
      setVendas([]);
    } finally {
      setLoading(false);
    }
  };

  // Carregamento inicial (busca vendedores, fornecedores e vendas iniciais)
  useEffect(() => {
    const initialLoad = async () => {
        setLoading(true);
        try {
            const params = {};
            const [vendasRes, vendedoresRes, fornecedoresRes] = await Promise.all([
                   axiosInstance.get('/vendas', { params }), // Vendas iniciais
                   axiosInstance.get('/vendedores'),
                   axiosInstance.get('/fornecedores') 
            ]);
            
            const fetchedVendas = vendasRes.data || [];
            const sortedVendas = fetchedVendas.sort((a, b) => {
              return new Date(b.data_venda) - new Date(a.data_venda);
            });
            setVendas(sortedVendas);

            setVendedores(vendedoresRes.data || []);
            setFornecedores(fornecedoresRes.data || []); 
        } catch(error) {
             toast.error('Erro ao carregar dados iniciais');
             setVendas([]);
             setVendedores([]);
             setFornecedores([]);
        } finally {
             setLoading(false);
        }
    };
    initialLoad();
  }, []); // Roda apenas uma vez

  // Função para o botão "Filtrar"
  const handleFiltrarClick = () => {
    loadVendas(filters);
  };

  // Função para o botão "Limpar Filtros"
  const handleClearFilters = () => {
    setFilters(initialFilters); 
    loadVendas(initialFilters); 
  };

  // Handlers para os campos de filtro
  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  // *** MUDANÇA: Corrigido para aceitar { startDate, endDate } ***
  const handleDateRangeChange = ({ startDate, endDate }) => {
    setFilters(prev => ({
      ...prev,
      date: { 
        from: startDate, 
        to: endDate 
      }
    }));
  };
  // *** FIM DA MUDANÇA ***
  
  // Handlers de Ação da Tabela
  const handleShowDetails = (venda) => {
    setSelectedVenda(venda);
    setIsModalOpen(true);
  };

  const handleDelete = async (vendaId) => {
    try {
      await axiosInstance.delete(`/vendas/${vendaId}`);
      toast.success('Venda deletada com sucesso!');
      loadVendas(filters); // Recarrega com os filtros atuais
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar venda');
    }
  };

  // Formatadores (Helpers)
  const getCategoriaLabel = (categoria) => {
    const labels = { AEREO: 'Aéreo', HOSPEDAGEM: 'Hospedagem', PACOTE: 'Pacote' };
    return labels[categoria] || categoria;
  };

  const formatCurrency = (value) => {
    return (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };
  
  const formatDate = (dateString) => {
     if (!dateString) return 'N/A';
     try {
        return format(parseISO(dateString), 'dd/MM/yyyy', { locale: ptBR });
     } catch (error) {
        return new Date(dateString).toLocaleDateString('pt-BR');
     }
  };
  
  // Paginação
  const totalPages = Math.ceil(vendas.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, vendas.length);
  // useMemo para calcular as vendas da página atual
  const currentVendas = useMemo(() => {
      return vendas.slice(startIndex, endIndex);
  }, [vendas, currentPage, startIndex, endIndex]);


  const goToPreviousPage = () => { setCurrentPage((prev) => Math.max(prev - 1, 1)); };
  const goToNextPage = () => { setCurrentPage((prev) => Math.min(prev + 1, totalPages)); };

  // --- FUNÇÃO DE DOWNLOAD DO VOUCHER ---
  const handleDownloadVoucher = async (vendaId, localizador) => {
    try {
      toast.info("Gerando PDF...");
      
      // AQUI ESTÁ O SEGREDO: { responseType: 'blob' }
      // Isso diz ao navegador para baixar os dados cruamente, sem tentar ler como texto.
      const response = await axiosInstance.get(`/vendas/${vendaId}/voucher`, {
        responseType: 'blob', 
      });

      // Cria um link temporário para forçar o download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Nome do arquivo
      const fileName = `Voucher_${localizador || vendaId}.pdf`;
      link.setAttribute('download', fileName);
      
      // Adiciona ao corpo, clica e remove (Truque padrão de download)
      document.body.appendChild(link);
      link.click();
      
      // Limpeza
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url); // Libera memória

      toast.success("Voucher baixado com sucesso!");
    } catch (error) {
      console.error("Erro ao baixar voucher:", error);
      toast.error("Erro ao gerar o PDF.");
    }
  };

  return (
    <div>
      <div className="w-full max-w-full px-2 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2 pt-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vendas</h1>
            <p className="text-gray-600">Gerencie suas vendas</p>
          </div>
          <Button data-testid="new-sale-button" onClick={() => navigate('/vendas/nova')} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Venda
          </Button>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Filtros</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* LINHA 1 */}
            <div className="md:col-span-2">
              <Label>Buscar por Cliente ou Localizador</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  data-testid="search-input-vendas"
                  placeholder="Digite o nome ou localizador..."
                  value={filters.termo_busca}
                  onChange={(e) => setFilters({ ...filters, termo_busca: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <Label>Período</Label>
              <DateRangePicker 
                startDate={filters.date.from}
                endDate={filters.date.to}
                onChange={handleDateRangeChange}
                placeholder="Selecione o período"
                className="mt-1"
              />
            </div>

            {/* LINHA 2 */}
            <div>
              <Label>Categoria</Label>
              <Select value={filters.categoria || "TODAS"} onValueChange={(value) => setFilters({ ...filters, categoria: value === "TODAS" ? "" : value })}>
                <SelectTrigger data-testid="filter-category-vendas" className="mt-1">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TODAS">Todas</SelectItem>
                  <SelectItem value="AEREO">Aéreo</SelectItem>
                  <SelectItem value="HOSPEDAGEM">Hospedagem</SelectItem>
                  <SelectItem value="PACOTE">Pacote</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Vendedor</Label>
              <Select value={filters.vendedor_id || "TODAS"} onValueChange={(value) => setFilters({ ...filters, vendedor_id: value === "TODAS" ? "" : value })}>
                <SelectTrigger data-testid="filter-seller-vendas" className="mt-1">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TODAS">Todos</SelectItem>
                  {(vendedores || []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Fornecedor</Label>
              <Select value={filters.fornecedor_id || "TODAS"} onValueChange={(value) => setFilters({ ...filters, fornecedor_id: value === "TODAS" ? "" : value })}>
                <SelectTrigger data-testid="filter-fornecedor-vendas" className="mt-1">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TODAS">Todos</SelectItem>
                  {(fornecedores || []).map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Botões de Ação */}
            <div className="flex items-end gap-2">
              <Button data-testid="filter-clear-vendas" onClick={handleClearFilters} variant="outline" className="w-full">
                <X className="w-4 h-4 mr-2" />
                Limpar
              </Button>
              <Button data-testid="filter-apply-vendas" onClick={handleFiltrarClick} className="w-full" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                Filtrar
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
             <p className="mt-4 text-gray-600">A carregar vendas...</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
  <table className="w-full text-xs text-left"> {/* Removi min-w-[768px] para tentar caber na tela */}
    <thead className="bg-gray-50 border-b border-gray-200">
      <tr>
        {/* AJUSTE 1: Diminuí o px-6 para px-3 e py-3 para py-2 */}
        <th className="px-3 py-2 font-medium text-gray-500 uppercase tracking-wider">Loc.</th>
        <th className="px-3 py-2 font-medium text-gray-500 uppercase tracking-wider">Clientes</th>
        <th className="px-3 py-2 font-medium text-gray-500 uppercase tracking-wider">Categ.</th>
        <th className="px-3 py-2 font-medium text-gray-500 uppercase tracking-wider">Vendedor</th>
        <th className="px-3 py-2 font-medium text-gray-500 uppercase tracking-wider">Fornecedor</th>
        <th className="px-3 py-2 font-medium text-gray-500 uppercase tracking-wider">Valor</th>
        <th className="px-3 py-2 font-medium text-gray-500 uppercase tracking-wider">Data</th>
        <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">Ações</th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {currentVendas.map((venda) => (
        <tr key={venda.id} className="hover:bg-gray-50 transition-colors">
          
          {/* Localizador */}
          <td className="px-3 py-2 text-xs font-medium text-gray-900 whitespace-nowrap">
            {venda.localizador}
          </td>
          
          {/* Clientes - Adicionei max-w e truncate para não estourar */}
          <td className="px-3 py-2 text-xs text-gray-600 max-w-[150px] truncate">
            {(() => {
              const nomes = venda.clientes_nomes || [];
              if (nomes.length === 0) return 'N/A';
              if (nomes.length === 1) return nomes[0];
              const outros = nomes.length - 1;
              return (
                <span title={(nomes || []).join(', ')}>
                  {nomes[0]} <span className="text-xs text-gray-400">(+{outros})</span>
                </span>
              );
            })()}
          </td>

          {/* Categoria - Badge menor */}
          <td className="px-3 py-2 text-xs">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${ // Diminuí fonte e padding do badge
                venda.categoria === 'AEREO' ? 'bg-blue-100 text-blue-800' :
                venda.categoria === 'HOSPEDAGEM' ? 'bg-yellow-100 text-yellow-800' :
                venda.categoria === 'PACOTE' ? 'bg-purple-100 text-purple-800' :
                'bg-gray-100 text-gray-800'
            }`}>
              {getCategoriaLabel(venda.categoria)}
            </span>
          </td>

          {/* Vendedor - Truncar nomes longos */}
          <td className="px-3 py-2 text-xs text-gray-600 max-w-[100px] truncate" title={venda.vendedor_nome}>
            {venda.vendedor_nome || '-'}
          </td>

          {/* Fornecedor - Truncar nomes longos */}
          <td className="px-3 py-2 text-xs text-gray-600 max-w-[100px] truncate" title={venda.fornecedor_nome}>
            {venda.fornecedor_nome || '-'}
          </td>

          {/* Valor */}
          <td className="px-3 py-2 text-xs font-medium text-gray-900 whitespace-nowrap">
            {formatCurrency(venda.valor_total)}
          </td>
          
          {/* Data */}
          <td className="px-3 py-2 text-xs text-gray-600 text-xs whitespace-nowrap">
            {formatDate(venda.data_venda)}
          </td>
          
          {/* Ações - Botões menores */}
          <td className="px-3 py-2 text-xs text-right">
            <div className="flex items-center justify-end gap-1"> {/* Gap menor */}
               <Button variant="ghost" size="icon" className="h-6 w-6" 
                  onClick={() => handleDownloadVoucher(venda.id, venda.localizador)} 
                  title="Baixar Voucher"> <Printer className="h-3.5 w-3.5 text-purple-600" /> </Button>
               <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleShowDetails(venda)} title="Ver"> <Eye className="h-3.5 w-3.5 text-blue-600" /> </Button>
               <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => navigate(`/vendas/editar/${venda.id}`)} title="Editar"> <Pencil className="w-3.5 w-3.5" /> </Button>
               <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6" title="Excluir"> <Trash2 className="w-3.5 h-3.5 text-red-600" /> </Button>
                </AlertDialogTrigger>
                {/* ... conteúdo do modal de excluir ... */}
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle><AlertDialogDescription>Deseja deletar esta venda? Todos os lançamentos financeiros associados ("PENDENTES" e "PAGOS") serão excluídos permanentemente.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(venda.id)} style={{ background: '#ef4444' }}>Deletar</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>

            {vendas.length === 0 && !loading && (
              <p className="text-center text-gray-500 py-4">Nenhuma venda encontrada.</p>
            )}

             {/* Controles de Paginação */}
             {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                    <div className="flex flex-1 justify-between sm:hidden">
                        <Button onClick={goToPreviousPage} disabled={currentPage === 1} variant="outline">Anterior</Button>
                        <Button onClick={goToNextPage} disabled={currentPage === totalPages} variant="outline">Próxima</Button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Mostrando <span className="font-medium">{vendas.length === 0 ? 0 : startIndex + 1}</span> a <span className="font-medium">{endIndex}</span> de{' '}
                                <span className="font-medium">{vendas.length}</span> resultados
                            </p>
                        </div>
                        <div>
                                <Button onClick={goToPreviousPage} disabled={currentPage === 1} variant="outline" className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
                                    <span className="sr-only">Anterior</span><ChevronLeft className="h-5 w-5" aria-hidden="true" />
                                </Button>
                                <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">Página {totalPages > 0 ? totalPages : 1}</span>
                                <Button onClick={goToNextPage} disabled={currentPage === totalPages} variant="outline" className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
                                    <span className="sr-only">Próxima</span><ChevronRight className="h-5 w-5" aria-hidden="true" />
                                </Button>
                        </div>
                    </div>
                </div>
             )}
          </div>
        )}
      </div>
      
      {/* Modal de Detalhes */}
      {selectedVenda && (
        <DetalhesVendaModal
          venda={selectedVenda}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}